import { type Type, TYPES } from "@/autogen/analytics_key";
import type { Newsletter } from "@/types/newsletter";

import UNTYPED_METRIC_TO_SPEC from "./specs.json";

export type DateRange = { start: string; end: string };

export type Metric = Type;
const METRICS = TYPES;
export type Period = "day" | "week" | "month" | "year";

type MetricVariant = "timeseries" | "proportion" | "qualitative";

type MetricValue = "count" | "percentage" | "monetary";

export type MetricSpec = {
  name: string;
  variant: MetricVariant;
  value: MetricValue;
  behavior: "sum" | "average";
  description: string;
  hidden?: (newsletter: Newsletter) => boolean;

  route?: string; // e.g. `/subscribers`
  dateParameter?: string; // e.g. `upgrade_date`
};

export const METRIC_TO_SPEC: {
  [key in Metric]: MetricSpec;
} = UNTYPED_METRIC_TO_SPEC as any;

export const METRIC_TO_HIDDEN: {
  [key in Metric]?: (newsletter: Newsletter) => boolean;
} = {
  paid_subscribers: (newsletter) =>
    newsletter?.paid_subscriptions_status !== "active",
  paid_subscribers_by_price: (newsletter) =>
    newsletter?.paid_subscriptions_status !== "active",
  page_views: (newsletter) => !newsletter?.should_track_page_views,
  clicks: (newsletter) => !newsletter?.should_track_clicks_on_emails,
  popular_urls: (newsletter) => !newsletter?.should_track_clicks_on_emails,
  opens: (newsletter) => !newsletter?.should_track_opens_on_emails,
  open_rate: (newsletter) => !newsletter?.should_track_opens_on_emails,
  click_rate: (newsletter) => !newsletter?.should_track_clicks_on_emails,
  browsers: (newsletter) => !newsletter?.should_track_clicks_on_emails,
  countries: (newsletter) => !newsletter?.should_track_clicks_on_emails,
  devices: (newsletter) => !newsletter?.should_track_clicks_on_emails,
  page_views_by_email: (newsletter) => !newsletter?.should_track_page_views,
  comments: (newsletter) => !newsletter?.enabled_features?.includes("comments"),
  comments_by_email: (newsletter) =>
    !newsletter?.enabled_features?.includes("comments"),
  confirmation_rate: (newsletter) => !newsletter?.should_require_double_optin,
  gross_volume: (newsletter) =>
    newsletter?.paid_subscriptions_status !== "active",
  automation_click_rate: (newsletter) =>
    !newsletter?.should_track_clicks_on_emails ||
    !newsletter.enabled_features?.includes("automations"),
  automation_open_rate: (newsletter) =>
    !newsletter?.should_track_opens_on_emails ||
    !newsletter.enabled_features?.includes("automations"),
  automation_clicks: (newsletter) =>
    !newsletter?.should_track_clicks_on_emails ||
    !newsletter.enabled_features?.includes("automations"),
  automation_opens: (newsletter) =>
    !newsletter?.should_track_opens_on_emails ||
    !newsletter.enabled_features?.includes("automations"),
  automation_deliveries: (newsletter) =>
    !newsletter.enabled_features?.includes("automations"),
};

export const generateRelevantMetrics = (newsletter: Newsletter) => {
  return METRICS.filter((metric) =>
    METRIC_TO_HIDDEN[metric] ? !METRIC_TO_HIDDEN[metric]!(newsletter) : true
  );
};
